/**
 * CX Leader Insights — Phase 2
 *
 * Renders ONLY for role === 'cx_leader'.
 * Backend: GET /api/role-insights/cx-leader
 *          GET /api/role-insights/cx-leader/lead/{lead_id}    (sidebar)
 *          POST /api/role-insights/cx-leader/lead/{lead_id}/ota/{i}/assign
 *
 * Layout:
 *   • 10 KPI cards (Total Accounts, Assigned Listings, Total OTAs,
 *                   Completed OTAs, Pending OTAs, Integration,
 *                   Late Completion, On-Time Rate %, Currently Overdue,
 *                   Avg Completion Time)
 *   • Properties table — ONLY accounts whose Master Form is complete
 *   • Action sidebar (per property) — OTA detail + Assign-to-CX dropdown
 *   • User Performance table — all CX members
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, ListChecks, Globe2, CheckCircle2, AlertTriangle, Link2,
  Percent, Hourglass, Timer, Briefcase, RefreshCw, Loader2, X,
  ExternalLink, ChevronRight, ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import OffboardingInsightsCards from './OffboardingInsightsCards';
import DateRangeFilter, { rangeParams } from '../../components/DateRangeFilter';
import MasterFormCards from '../../components/masterform/MasterFormCards';
import InsightListPopup from '../../components/InsightListPopup';
import { userHasRole } from '../../utils/roleHelpers';

const SLA_BADGE = {
  on_time: { label: 'On Time', cls: 'bg-emerald-100 text-emerald-700' },
  late:    { label: 'Late',    cls: 'bg-rose-100 text-rose-700' },
  overdue: { label: 'Overdue', cls: 'bg-red-100 text-red-700' },
  pending: { label: 'Pending', cls: 'bg-gray-100 text-gray-600' },
};

// Phase 7 — escalation palette shared with CX Insights.
const OVERDUE_PALETTE = {
  Late:     'bg-amber-100 text-amber-700 border-amber-200',
  Urgent:   'bg-orange-100 text-orange-700 border-orange-200',
  Critical: 'bg-rose-100 text-rose-700 border-rose-200',
};

const Card = ({ label, value, Icon, accent, suffix, testId, onClick, active }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-left bg-white rounded-xl border ${active ? 'border-teal-400 ring-2 ring-teal-100' : 'border-gray-200'} p-4 flex items-center gap-3 shadow-sm transition hover:border-teal-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-teal-200 w-full`}
    data-testid={testId}
  >
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent.bg}`}>
      <Icon size={20} className={accent.fg} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[11px] font-medium text-gray-500 truncate">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-0.5" data-testid={`${testId}-value`}>
        {value}{suffix && <span className="text-xs font-medium text-gray-500 ml-1">{suffix}</span>}
      </p>
    </div>
  </button>
);

// 1D — On-Time Rate card with an employee-wise breakdown on hover.
const OnTimeCard = ({ value, breakdown }) => {
  const rows = (breakdown || []).slice().sort((a, b) => (b.rate || 0) - (a.rate || 0));
  return (
    <div className="relative group" data-testid="cxl-card-on-time-rate">
      <div className="text-left bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 shadow-sm transition hover:border-emerald-300 hover:shadow-md w-full cursor-default">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-emerald-50">
          <Percent size={20} className="text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-medium text-gray-500 truncate">On-Time Rate</p>
          <p className="text-2xl font-bold text-gray-900 mt-0.5" data-testid="cxl-card-on-time-rate-value">
            {value}<span className="text-xs font-medium text-gray-500 ml-1">%</span>
          </p>
        </div>
      </div>
      {/* Hover breakdown */}
      <div className="absolute z-30 left-0 top-full mt-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-100 p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity"
        data-testid="cxl-on-time-breakdown">
        <p className="text-xs font-bold text-gray-700 mb-2">Employee On-Time Rate</p>
        {rows.length === 0 ? (
          <p className="text-xs text-gray-400 py-2">No employee performance data for this filter.</p>
        ) : (
          <div className="max-h-64 overflow-auto divide-y divide-gray-50">
            {rows.map((r, i) => (
              <div key={`${r.name}-${i}`} className="flex items-center justify-between py-1.5" data-testid={`cxl-on-time-row-${i}`}>
                <div className="min-w-0 flex items-center gap-2">
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${r.role === 'QC' ? 'bg-fuchsia-100 text-fuchsia-700' : 'bg-sky-100 text-sky-700'}`}>{r.role}</span>
                  <span className="text-xs text-gray-700 truncate" title={r.name}>{r.name}</span>
                </div>
                <span className={`text-xs font-bold ${r.rate >= 80 ? 'text-emerald-600' : r.rate >= 50 ? 'text-amber-600' : 'text-rose-600'}`}>{r.rate}%</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


const fmtMinutes = (mins) => {
  if (!mins || mins <= 0) return '—';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return m ? `${h}h ${m}m` : `${h}h`;
};

const ActionSidebar = ({ leadId, onClose, refreshParent }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingIdx, setSavingIdx] = useState(null);

  const authHeader = () => {
    const t = localStorage.getItem('token') || localStorage.getItem('access_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/role-insights/cx-leader/lead/${leadId}`, { headers: authHeader() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDetail(await res.json());
    } catch (e) { toast.error(e.message || 'Failed to load detail'); }
    finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const assign = async (otaIndex, assigneeId) => {
    setSavingIdx(otaIndex);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/role-insights/cx-leader/lead/${leadId}/ota/${otaIndex}/assign`,
        { method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ assignee_id: assigneeId || null }) },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      toast.success(assigneeId ? 'Assigned' : 'Unassigned');
      await load();
      refreshParent?.();
    } catch (e) { toast.error(e.message || 'Assign failed'); }
    finally { setSavingIdx(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="cx-leader-action-sidebar">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-800 text-base">{detail?.hotel_name || '…'}</h3>
            <p className="text-xs text-gray-500 mt-0.5">OTA assignments & SLA</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-lg" data-testid="cx-leader-sidebar-close"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading && <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-teal-500" /></div>}
          {!loading && (detail?.items || []).length === 0 && (
            <p className="text-sm text-gray-400 text-center py-10">No OTAs on this account yet.</p>
          )}
          {(detail?.items || []).map((item) => {
            const sla = SLA_BADGE[item.sla_status] || SLA_BADGE.pending;
            return (
              <div key={item.ota_index} className="border border-gray-200 rounded-xl p-4 space-y-3" data-testid={`cx-leader-sidebar-ota-${item.ota_index}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{item.ota_name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Hotel ID: <span className="font-medium text-gray-700">{item.hotel_id || '—'}</span></p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${sla.cls}`}>{sla.label}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-gray-400">Start</p>
                    <p className="text-gray-700">{item.timer_start ? new Date(item.timer_start).toLocaleString() : '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">End</p>
                    <p className="text-gray-700">{item.completion_date ? new Date(item.completion_date).toLocaleString() : '—'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-400">Deadline ({item.sla_hours}h SLA)</p>
                    <p className="text-gray-700">{item.deadline ? new Date(item.deadline).toLocaleString() : '—'}</p>
                  </div>
                </div>
                {item.listing_url && (
                  <a href={item.listing_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-teal-600 hover:underline">
                    <ExternalLink size={11} /> {item.listing_url}
                  </a>
                )}
                <div>
                  <label className="text-[11px] font-semibold text-gray-500 mb-1 block">Assign to</label>
                  <select
                    value={item.assigned_to || ''}
                    disabled={item.url_locked || savingIdx === item.ota_index}
                    onChange={(e) => assign(item.ota_index, e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white disabled:bg-gray-50 disabled:cursor-not-allowed"
                    data-testid={`cx-leader-sidebar-assign-${item.ota_index}`}
                  >
                    <option value="">— Unassigned —</option>
                    {(detail?.cx_users || []).map(u => (
                      <option key={u.id || u.email} value={u.id || u.email}>{u.name || u.email}</option>
                    ))}
                  </select>
                  {item.url_locked && <p className="text-[10px] text-gray-400 mt-1">Locked after submission — cannot reassign.</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Phase 12 — QC Assignment Sidebar (CX Leader → assign QC user per OTA)
const QCAssignSidebar = ({ leadId, hotelName, qcUsers, onClose }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingIdx, setSavingIdx] = useState(null);

  const authHeader = () => {
    const t = localStorage.getItem('token') || localStorage.getItem('access_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/role-insights/cx-leader/lead/${leadId}`, { headers: authHeader() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const detail = await res.json();
      setItems(detail.items || []);
    } catch (e) { toast.error(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const assignQC = async (otaIndex, assigneeId) => {
    setSavingIdx(otaIndex);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/qc/lead/${leadId}/ota/${otaIndex}/assign`,
        { method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify({ assignee_id: assigneeId }) },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      toast.success('QC assigned');
      await load();
    } catch (e) { toast.error(e.message || 'Assign failed'); }
    finally { setSavingIdx(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="cxl-qc-assign-sidebar">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white shadow-2xl flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200 bg-fuchsia-50 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-800 text-base flex items-center gap-2"><ShieldCheck size={16} className="text-fuchsia-600" /> Assign QC — {hotelName || ''}</h3>
            <p className="text-xs text-gray-500 mt-0.5">QC can be assigned only AFTER the listing is completed & URL is submitted.</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-fuchsia-100 rounded-lg" data-testid="cxl-qc-assign-close"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading && <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-fuchsia-500" /></div>}
          {!loading && items.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-10">No OTAs on this account yet.</p>
          )}
          {!loading && items.map((item) => {
            const isCompletedOTA = (item.status === 'completed') && Boolean(item.listing_url);
            return (
              <div key={item.ota_index} className="border border-gray-200 rounded-xl p-4 space-y-3" data-testid={`cxl-qc-ota-${item.ota_index}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{item.ota_name}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Listing: <span className={`font-medium ${isCompletedOTA ? 'text-emerald-600' : 'text-amber-600'}`}>{item.status || '—'}</span></p>
                  </div>
                  {!isCompletedOTA && (
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-500">QC locked</span>
                  )}
                </div>
                {!isCompletedOTA ? (
                  <p className="text-xs text-gray-400 italic">QC Assignment becomes available once the CX submits the Listing URL and OTA status is Completed.</p>
                ) : (
                  <div>
                    <label className="text-[11px] font-semibold text-gray-500 mb-1 block">Assign to QC user</label>
                    <select
                      value={item.qc_assigned_to || ''}
                      disabled={savingIdx === item.ota_index}
                      onChange={(e) => { if (e.target.value) assignQC(item.ota_index, e.target.value); }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white disabled:bg-gray-50"
                      data-testid={`cxl-qc-assign-${item.ota_index}`}
                    >
                      <option value="">— Select QC user —</option>
                      {qcUsers.map(u => (
                        <option key={u.id} value={u.id}>{u.name || u.id}</option>
                      ))}
                      {/* Phase 14.5 — surface the assigned QC user even when
                          they are not in the current qcUsers list (e.g.
                          role changed). Prevents the dropdown from falling
                          back to the placeholder after a successful assign. */}
                      {item.qc_assigned_to && !qcUsers.some(u => u.id === item.qc_assigned_to) && (
                        <option value={item.qc_assigned_to}>{item.qc_assigned_to_name || item.qc_assigned_to}</option>
                      )}
                    </select>
                    {qcUsers.length === 0 && (
                      <p className="text-[11px] text-amber-600 mt-1">No QC users found. Admin must create a user with role "qc" in User Management first.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const CXLeaderInsights = ({ hideOffboardingCards = false }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [qcData, setQcData] = useState(null);
  const [qcUsers, setQcUsers] = useState([]);
  const [qcPerf, setQcPerf] = useState({ rows: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidebarLeadId, setSidebarLeadId] = useState(null);
  const [qcAssignTarget, setQcAssignTarget] = useState(null); // { leadId, hotelName }
  const [range, setRange] = useState('this_week');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  // Phase 10 — card click drills the Properties table.
  const [cardFilter, setCardFilter] = useState(null);
  const [popup, setPopup] = useState(null); // { title, columns, rows }
  const propertiesRef = useRef(null);
  const openCardDrilldown = (key) => {
    setCardFilter((prev) => (prev === key ? null : key));
    setTimeout(() => {
      propertiesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const authHeader = () => {
    const t = localStorage.getItem('token') || localStorage.getItem('access_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams(rangeParams(range, customFrom, customTo)).toString();
      const [resA, resB, resC, resD] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/role-insights/cx-leader?${qs}`, { headers: authHeader() }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/qc/cx-leader-stats?${qs}`, { headers: authHeader() }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/lookup`, { headers: authHeader() }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/qc/performance-table`, { headers: authHeader() }),
      ]);
      if (!resA.ok) throw new Error(`HTTP ${resA.status}`);
      setData(await resA.json());
      if (resB.ok) setQcData(await resB.json()); else setQcData({ cards: {}, per_lead: {} });
      if (resC.ok) {
        const all = await resC.json();
        // iter269 — Shared role helper; case-insensitive & multi-role
        // safe. Adding a new role requires zero changes here.
        setQcUsers((all || []).filter(u => userHasRole(u, 'qc')));
      } else { setQcUsers([]); }
      if (resD.ok) setQcPerf(await resD.json()); else setQcPerf({ rows: [] });
    } catch (e) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [range, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  const cards = data?.cards || {};
  const rawProperties = data?.properties || [];
  const userPerformance = data?.user_performance || [];
  const completedListings = data?.completed_listings || [];
  const listings = data?.listings || [];
  const qcRows = qcData?.rows || { total: [], assigned: [], completed: [], pending: [] };
  // Popups derive from the SAME filtered datasets that produced the cards.
  const listingPopupCols = [
    { key: 'hotel_name', label: 'Hotel' },
    { key: 'ota_name', label: 'OTA' },
    { key: 'cx_name', label: 'CX' },
    { key: 'overdue_label', label: 'Overdue' },
    { key: 'listing_url', label: 'URL' },
  ];
  const qcPopupCols = [
    { key: 'hotel_name', label: 'Hotel' },
    { key: 'ota_name', label: 'OTA' },
    { key: 'status', label: 'QC Status' },
    { key: 'overdue_label', label: 'Overdue' },
    { key: 'listing_url', label: 'URL' },
  ];
  const openListingPopup = (title, status) => setPopup({ title, columns: listingPopupCols, rows: listings.filter((r) => r.status === status) });
  const openQcPopup = (title, key) => {
    const rows = key === 'unassigned' ? (qcRows.total || []).filter((r) => !r.assigned) : (qcRows[key] || []);
    setPopup({ title, columns: qcPopupCols, rows });
  };

  // 1C — clicking a card opens the Tasks tab on the matching sub-tab with the
  // active date filter preserved (so card count == Tasks tab rows == popup).
  const gotoTask = (section, tab) => {
    const qp = new URLSearchParams({ section, tab, ...rangeParams(range, customFrom, customTo) });
    navigate(`/tasks?${qp.toString()}`);
  };
  // 1C — CX Listings card counts derive from the SAME `listings` dataset the
  // Tasks tabs use, guaranteeing card == tab == popup (no count mismatch).
  const cxAssignedCount = listings.filter((r) => r.status === 'assigned' || r.status === 'in_progress').length;
  const cxCompletedCount = listings.filter((r) => r.status === 'completed').length;
  const cxUnderReviewCount = listings.filter((r) => r.status === 'under_review').length;
  const cxRejectedCount = listings.filter((r) => r.status === 'rejected').length;
  // 1D — On-Time Rate hover: per-employee (CX + QC) breakdown.
  const onTimeBreakdown = [
    ...userPerformance.map((u) => ({ name: u.name, role: u.role || 'CX', rate: u.on_time_rate ?? 0 })),
    ...((qcData?.employee_on_time) || []).map((u) => ({ name: u.name, role: 'QC', rate: u.on_time_rate ?? 0 })),
  ];
  // Phase 10 — filter the Properties table by the active card.
  const properties = useMemo(() => {
    if (!cardFilter) return rawProperties;
    return rawProperties.filter((p) => {
      switch (cardFilter) {
        case 'completed':       return (p.complete || 0) > 0;
        case 'pending':         return (p.pending || 0) > 0;
        case 'integration':     return (p.integration || 0) > 0;
        case 'overdue':         return Boolean((p.overdue_label || '').trim()) || (p.overdue_time || '').length > 0;
        case 'late_completion': return p.overdue_label === 'Late' || p.overdue_label === 'Urgent' || p.overdue_label === 'Critical';
        default: return true;
      }
    });
  }, [rawProperties, cardFilter]);

  return (
    <div className="space-y-5" data-testid="cx-leader-insights">
      <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">CX Leader Insights</h2>
          <p className="text-sm text-gray-500 mt-0.5">OTA assignments, SLA tracking and team performance.</p>
        </div>
        <button onClick={load} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          data-testid="cx-leader-refresh">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <DateRangeFilter value={range} onChange={setRange} customFrom={customFrom} customTo={customTo}
        onCustomFrom={setCustomFrom} onCustomTo={setCustomTo} testId="cx-leader-date-filter" />

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3" data-testid="cx-leader-error">{error}</div>}

      {loading && !data ? (
        <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 size={24} className="animate-spin" /></div>
      ) : (
        <>
          {/* Phase 4 — Master Forms (Onboarding Form follow-up) KPI cards */}
          <MasterFormCards range={range} customFrom={customFrom} customTo={customTo} />

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-testid="cxl-cards-grid">
            <Card label="Assigned"        value={cxAssignedCount}  Icon={ListChecks}    accent={{ bg: 'bg-sky-50', fg: 'text-sky-600' }} testId="cxl-card-assigned" onClick={() => gotoTask('cx-listings', 'assigned')} />
            <Card label="Under Review"    value={cxUnderReviewCount}       Icon={Hourglass}     accent={{ bg: 'bg-amber-50', fg: 'text-amber-600' }} testId="cxl-card-under-review" onClick={() => gotoTask('cx-listings', 'under_review')} />
            <Card label="Rejected"        value={cxRejectedCount}           Icon={AlertTriangle} accent={{ bg: 'bg-rose-50', fg: 'text-rose-600' }} testId="cxl-card-rejected" onClick={() => gotoTask('cx-listings', 'rejected')} />
            <Card label="Total OTAs"      value={cards.total_otas ?? 0}         Icon={Globe2}        accent={{ bg: 'bg-indigo-50', fg: 'text-indigo-600' }} testId="cxl-card-total-otas" onClick={() => openCardDrilldown(null)} />
            <Card label="Completed"       value={cxCompletedCount}     Icon={CheckCircle2}  accent={{ bg: 'bg-emerald-50', fg: 'text-emerald-600' }} testId="cxl-card-completed" onClick={() => gotoTask('cx-listings', 'completed')} />
            <Card label="Pending OTAs"    value={cards.pending_otas ?? 0}       Icon={AlertTriangle} accent={{ bg: 'bg-amber-50', fg: 'text-amber-600' }} testId="cxl-card-pending" onClick={() => openCardDrilldown('pending')} active={cardFilter === 'pending'} />
            <Card label="Integration"     value={cards.integration ?? 0}        Icon={Link2}         accent={{ bg: 'bg-violet-50', fg: 'text-violet-600' }} testId="cxl-card-integration" onClick={() => openCardDrilldown('integration')} active={cardFilter === 'integration'} />
            <Card label="Late Completion" value={cards.late_completion ?? 0}    Icon={AlertTriangle} accent={{ bg: 'bg-rose-50', fg: 'text-rose-600' }} testId="cxl-card-late" onClick={() => openCardDrilldown('late_completion')} active={cardFilter === 'late_completion'} />
            <OnTimeCard value={cards.on_time_rate ?? 0} breakdown={onTimeBreakdown} />
            <Card label="Overdue"         value={cards.currently_overdue ?? 0}  Icon={Hourglass}     accent={{ bg: 'bg-red-50', fg: 'text-red-600' }} testId="cxl-card-overdue" onClick={() => openCardDrilldown('overdue')} active={cardFilter === 'overdue'} />
            <Card label="Avg Time"        value={fmtMinutes(cards.average_completion_minutes)} Icon={Timer} accent={{ bg: 'bg-slate-100', fg: 'text-slate-700' }} testId="cxl-card-avg" onClick={() => openCardDrilldown(null)} />
            {/* Phase 12 — QC cards (Total >= Assigned >= Completed) */}
            <Card label="Total QC"        value={qcData?.cards?.total_qc ?? 0}     Icon={ShieldCheck}   accent={{ bg: 'bg-fuchsia-50', fg: 'text-fuchsia-600' }} testId="cxl-card-qc-total" onClick={() => gotoTask('qc', 'total')} />
            <Card label="Assigned QC"     value={qcData?.cards?.assigned_qc ?? 0}  Icon={ListChecks}    accent={{ bg: 'bg-sky-50', fg: 'text-sky-600' }} testId="cxl-card-qc-assigned" onClick={() => gotoTask('qc', 'assigned')} />
            <Card label="Completed QC"    value={qcData?.cards?.completed_qc ?? 0} Icon={CheckCircle2}  accent={{ bg: 'bg-emerald-50', fg: 'text-emerald-600' }} testId="cxl-card-qc-completed" onClick={() => gotoTask('qc', 'completed')} />
            <Card label="Pending QC"      value={qcData?.cards?.pending_qc ?? 0}   Icon={AlertTriangle} accent={{ bg: 'bg-amber-50', fg: 'text-amber-600' }}     testId="cxl-card-qc-pending" onClick={() => gotoTask('qc', 'pending')} />
          </div>

          <div ref={propertiesRef} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="cxl-properties-table-wrap">
            <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between gap-3">
              <h3 className="text-sm font-bold text-gray-800">Properties (Master Form complete)</h3>
              <div className="flex items-center gap-3">
                {cardFilter && (
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-gray-500">Filtered:</span>
                    <span className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded-full font-medium" data-testid="cxl-card-filter-chip">
                      {cardFilter.replace(/_/g, ' ')}
                    </span>
                    <button onClick={() => setCardFilter(null)} className="text-gray-400 hover:text-gray-700" data-testid="cxl-card-filter-clear" aria-label="Clear filter">×</button>
                  </div>
                )}
                <span className="text-xs text-gray-400">{properties.length} rows</span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cxl-properties-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left">Hotel Name</th>
                    <th className="px-4 py-2.5 text-left">Lead Date</th>
                    <th className="px-4 py-2.5 text-left">Lead Owner</th>
                    <th className="px-4 py-2.5 text-right">OTA</th>
                    <th className="px-4 py-2.5 text-right">Complete</th>
                    <th className="px-4 py-2.5 text-right">New Listing</th>
                    <th className="px-4 py-2.5 text-right">Integration</th>
                    <th className="px-4 py-2.5 text-right">Pending</th>
                    <th className="px-4 py-2.5 text-left">Overdue</th>
                    <th className="px-4 py-2.5 text-left">Overdue Time</th>
                    <th className="px-4 py-2.5 text-right">Action</th>
                    {/* Phase 12 — QC columns */}
                    <th className="px-4 py-2.5 text-right bg-fuchsia-50/60">QC Complete</th>
                    <th className="px-4 py-2.5 text-right bg-fuchsia-50/60">QC Pending</th>
                    <th className="px-4 py-2.5 text-left bg-fuchsia-50/60">QC Overdue</th>
                    <th className="px-4 py-2.5 text-left bg-fuchsia-50/60">QC Overdue Time</th>
                    <th className="px-4 py-2.5 text-right bg-fuchsia-50/60">QC Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {properties.length === 0 && (
                    <tr><td colSpan={16} className="px-4 py-8 text-center text-gray-400 text-xs">No properties with master form completed yet.</td></tr>
                  )}
                  {properties.map((p) => {
                    const ov = (p.overdue_label || '').trim();
                    const ovCls = OVERDUE_PALETTE[ov] || null;
                    const qc = (qcData?.per_lead || {})[p.lead_id] || { complete: 0, pending: 0, label: '' };
                    const qcOv = (qc.label || '').trim();
                    const qcOvCls = OVERDUE_PALETTE[qcOv] || null;
                    return (
                    <tr key={p.lead_id} className="hover:bg-gray-50" data-testid={`cxl-prop-${p.lead_id}`}>
                      <td className="px-4 py-2.5 font-medium text-gray-800">{p.hotel_name}</td>
                      <td className="px-4 py-2.5 text-gray-600 text-xs">{p.lead_date || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-700 text-xs">{p.lead_owner || '—'}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{p.ota_total}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-emerald-600 font-semibold">{p.complete}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{p.new_listing}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{p.integration}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-amber-600 font-semibold">{p.pending}</td>
                      <td className="px-4 py-2.5" data-testid={`cxl-prop-overdue-${p.lead_id}`}>
                        {ov ? (
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${ovCls}`}>{ov}</span>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-600 whitespace-nowrap" data-testid={`cxl-prop-overdue-time-${p.lead_id}`}>
                        {p.overdue_time || <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <button onClick={() => setSidebarLeadId(p.lead_id)} className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-teal-600 text-white rounded-md hover:bg-teal-700" data-testid={`cxl-prop-action-${p.lead_id}`}>
                          Open <ChevronRight size={11} />
                        </button>
                      </td>
                      {/* Phase 12 — QC cells */}
                      <td className="px-4 py-2.5 text-right tabular-nums text-emerald-600 font-semibold bg-fuchsia-50/30" data-testid={`cxl-prop-qc-complete-${p.lead_id}`}>{qc.complete || 0}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-amber-600 font-semibold bg-fuchsia-50/30" data-testid={`cxl-prop-qc-pending-${p.lead_id}`}>{qc.pending || 0}</td>
                      <td className="px-4 py-2.5 bg-fuchsia-50/30" data-testid={`cxl-prop-qc-overdue-${p.lead_id}`}>
                        {qcOv ? (
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${qcOvCls}`}>{qcOv}</span>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-600 whitespace-nowrap bg-fuchsia-50/30" data-testid={`cxl-prop-qc-overdue-time-${p.lead_id}`}>—</td>
                      <td className="px-4 py-2.5 text-right bg-fuchsia-50/30">
                        <button onClick={() => setQcAssignTarget({ leadId: p.lead_id, hotelName: p.hotel_name })}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-fuchsia-600 text-white rounded-md hover:bg-fuchsia-700"
                          data-testid={`cxl-prop-qc-action-${p.lead_id}`}>
                          <ShieldCheck size={11} /> Assign
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="cxl-user-perf-table-wrap">
            <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
              <Users size={18} className="text-teal-600" />
              <h3 className="text-sm font-bold text-gray-800">User Performance</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cxl-user-perf-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left">CX Member</th>
                    <th className="px-4 py-2.5 text-right">Assigned</th>
                    <th className="px-4 py-2.5 text-right">Completed</th>
                    {/* iter263 — Pending column mirrors user_performance[].pending
                        (all non-completed items for this CX agent). */}
                    <th className="px-4 py-2.5 text-right">Pending</th>
                    <th className="px-4 py-2.5 text-left">Overdue</th>
                    <th className="px-4 py-2.5 text-right">Avg Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {userPerformance.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400 text-xs">No CX assignments yet.</td></tr>
                  )}
                  {userPerformance.map(u => {
                    const late = u.overdue_late || 0;
                    const urgent = u.overdue_urgent || 0;
                    const critical = u.overdue_critical || 0;
                    const hasAny = late + urgent + critical > 0;
                    return (
                    <tr key={u.name} className="hover:bg-gray-50" data-testid={`cxl-perf-${u.name}`}>
                      <td className="px-4 py-2.5 font-medium text-gray-800">{u.name}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums" data-testid={`cxl-perf-assigned-${u.name}`}>{u.assigned_properties}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-emerald-600 font-semibold">{u.completed_listings}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-amber-600 font-semibold" data-testid={`cxl-perf-pending-${u.name}`}>{u.pending ?? 0}</td>
                      <td className="px-4 py-2.5" data-testid={`cxl-perf-overdue-${u.name}`}>
                        {hasAny ? (
                          <div className="flex flex-wrap items-center gap-1">
                            {late > 0 && <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${OVERDUE_PALETTE.Late}`}>Late · {late}</span>}
                            {urgent > 0 && <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${OVERDUE_PALETTE.Urgent}`}>Urgent · {urgent}</span>}
                            {critical > 0 && <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${OVERDUE_PALETTE.Critical}`}>Critical · {critical}</span>}
                          </div>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{fmtMinutes(u.average_minutes)}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mega-Spec Item 10 — Completed OTA Listings (captured at Submit URL) */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="cxl-completed-listings-wrap">
            <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
              <Link2 size={18} className="text-teal-600" />
              <h3 className="text-sm font-bold text-gray-800">Completed OTA Listings</h3>
              <span className="text-xs text-gray-400 ml-auto">{completedListings.length} listings</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cxl-completed-listings-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left">Account</th>
                    <th className="px-4 py-2.5 text-left">OTA</th>
                    <th className="px-4 py-2.5 text-left">Property Name</th>
                    <th className="px-4 py-2.5 text-left">Hotel ID</th>
                    <th className="px-4 py-2.5 text-left">Listing URL</th>
                    <th className="px-4 py-2.5 text-left">CX</th>
                    <th className="px-4 py-2.5 text-left">Start</th>
                    <th className="px-4 py-2.5 text-left">Completed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {completedListings.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400 text-xs">No completed OTA listings yet.</td></tr>
                  )}
                  {completedListings.map((cl, i) => (
                    <tr key={`${cl.lead_id}-${i}`} className="hover:bg-gray-50" data-testid={`cxl-completed-listing-${i}`}>
                      <td className="px-4 py-2.5 font-medium text-gray-800">{cl.account_name || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-600">{cl.ota_name || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-700" data-testid={`cxl-cl-property-name-${i}`}>{cl.property_name || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-700" data-testid={`cxl-cl-hotel-id-${i}`}>{cl.hotel_id || '—'}</td>
                      <td className="px-4 py-2.5">
                        {cl.listing_url ? (
                          <a href={cl.listing_url} target="_blank" rel="noreferrer" className="text-teal-600 hover:underline inline-flex items-center gap-1" data-testid={`cxl-cl-url-${i}`}>
                            <Link2 size={12} /> View
                          </a>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{cl.cx_name || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">{cl.start_time ? new Date(cl.start_time).toLocaleString() : '—'}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">{cl.completion_time ? new Date(cl.completion_time).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Phase 13 — QC Performance Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid="cxl-qc-perf-table-wrap">
            <div className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2">
              <ShieldCheck size={18} className="text-fuchsia-600" />
              <h3 className="text-sm font-bold text-gray-800">QC Performance</h3>
              <span className="text-xs text-gray-400 ml-auto">Per-QC-user metrics</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cxl-qc-perf-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left">QC User Name</th>
                    <th className="px-4 py-2.5 text-right">Assigned</th>
                    <th className="px-4 py-2.5 text-right">Completed</th>
                    <th className="px-4 py-2.5 text-right">Overdue</th>
                    <th className="px-4 py-2.5 text-right">Avg Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(qcPerf.rows || []).length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400 text-xs">No QC users found. Admin must create a QC user in User Management first.</td></tr>
                  )}
                  {(qcPerf.rows || []).map(u => (
                    <tr key={u.user_id} className="hover:bg-gray-50" data-testid={`cxl-qc-perf-${u.user_id}`}>
                      <td className="px-4 py-2.5 font-medium text-gray-800">{u.user_name}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums" data-testid={`cxl-qc-perf-assigned-${u.user_id}`}>{u.assigned}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-emerald-600 font-semibold" data-testid={`cxl-qc-perf-completed-${u.user_id}`}>{u.completed}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-amber-600 font-semibold" data-testid={`cxl-qc-perf-overdue-${u.user_id}`}>{u.overdue}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums" data-testid={`cxl-qc-perf-avg-${u.user_id}`}>{fmtMinutes(u.avg_minutes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {sidebarLeadId && (
        <ActionSidebar leadId={sidebarLeadId} onClose={() => setSidebarLeadId(null)} refreshParent={load} />
      )}
      {qcAssignTarget && (
        <QCAssignSidebar
          leadId={qcAssignTarget.leadId}
          hotelName={qcAssignTarget.hotelName}
          qcUsers={qcUsers}
          onClose={() => { setQcAssignTarget(null); load(); }}
        />
      )}

      {popup && (
        <InsightListPopup
          title={popup.title}
          columns={popup.columns}
          rows={popup.rows}
          onClose={() => setPopup(null)}
          testId="cxl-insight-popup"
        />
      )}

      {/* Phase 16 — Offboarding cards (same CX-step visibility as cx role). */}
      {!hideOffboardingCards && <OffboardingInsightsCards />}
    </div>
  );
};

export default CXLeaderInsights;
